import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  inject,
  signal,
  OnInit,
  OnDestroy,
  Signal,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, isObservable, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { ModalService } from '../../services/modal.service';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { CreateModuleRightsDirective } from '../../../core/auth/directives/can-create-module.directive';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
  MatOptgroup,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { UtilityService } from '../../services/utility.service';
import { CascadingAutoFillService, AutoFillResult } from '../../services/cascading-auto-fill.service';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { resolveCreatedEntry, type CreateRunnerCloseResult } from '../../utils/create-runner-result.util';
import { PendingFile } from '../../../features/admin/modules/features/entries/entries.model';
import { FormlyErrorComponent } from '../formly-error.component';
import { FormlyErrorService } from '../../services/formly.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { FieldLabelRowComponent } from './field-label-row.component';
import { commitUserValue, markUserEdit } from './user-edit.util';

export const AUTOCOMPLETE_TEMPLATE = `
    <div class="w-full" [class.hidden]="isFieldHidden()">
      <div class="flex w-full items-center">
        <div class="flex-1 min-w-0">
        @if (props.requireSelection !== undefined) {
          <mat-form-field
            class="w-full"
            [floatLabel]="isDisabled() ? 'always' : 'auto'"
            [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
            @if (isLoading()) {
              <mat-spinner class="absolute right-8" diameter="24"> </mat-spinner>
            }
            @if (props.label) {
              <mat-label>
                <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
              </mat-label>
            }
            <input
              #searchInput
              type="text"
              [required]="props.required"
              [placeholder]="isDisabled() ? '' : props.placeholder"
              [formlyDataCy]="field"
              matInput
              [formControl]="searchField"
              [matAutocomplete]="auto"
              (focus)="onInputFocus()"
              class="form-control" />
            <!-- No display or spacing utilities here: dd-addon-suffix stacks the chevron and the
                 clear button in one cell so both land on Figma's 12px trailing inset, and a
                 Tailwind flex/mr-* on these two elements laid them out side by side instead, which
                 put the chevron 52px from the field edge. -->
            <div matSuffix class="dd-clear-suffix dd-addon-suffix">
              @if (filteredOptions().length && isDisabled() === false) {
                <mat-icon
                  class="dd-addon-suffix__passive dd-trailing-glyph"
                  svgIcon="feather:chevron-down"></mat-icon>
              }
              <app-clear-field-button
                [field]="field"
                [hasValue]="hasClearableValue()"
                [disabled]="isDisabled()"
                [readOnly]="isReadOnly()"
                [ariaLabel]="clearAriaLabel()"
                [focusTarget]="searchInput"
                (clearSelection)="clearValue()" />
            </div>

            <mat-autocomplete
              #auto="matAutocomplete"
              (opened)="onAutocompleteOpened()"
              (optionSelected)="onOptionSelected($event)"
              [panelWidth]="'auto'">
              @if (hasGrouping()) {
                @for (group of groupedOptions(); track group.name) {
                  <mat-optgroup [label]="group.name">
                    @for (option of group.options; track option.value?.id || option.value || option.label) {
                      <mat-option
                        [value]="option.label"
                        [disabled]="option.disabled === true">
                        <div class="flex items-center gap-2">
                          <div class="flex flex-col min-w-0 flex-1">
                            <span>{{ option.label }}</span>
                            @if (option.additionalInfoText) {
                              <div class="text-xs text-accent">{{ option.additionalInfoText }}</div>
                            } @else if (option.additionalInfo?.length) {
                              <div class="flex flex-wrap gap-2 text-xs text-accent">
                                @for (info of option.additionalInfo; track info.label) {
                                  <span>
                                    <span class="text-hint">{{ info.label }}:</span> {{ info.value }}
                                  </span>
                                }
                              </div>
                            }
                            @if (option.autoFillLinkedText) {
                              <div class="text-xs text-accent">{{ option.autoFillLinkedText }}</div>
                            }
                          </div>
                          @if (option.unavailableReason) {
                            <span class="ml-auto shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none bg-warning bg-opacity-12 text-warning-800">
                              {{ option.unavailableReason }}
                            </span>
                          }
                        </div>
                      </mat-option>
                    }
                  </mat-optgroup>
                }
              } @else {
                @for (
                  option of filteredOptions();
                  track option.value?.id || option.value || option.value || option.label
                ) {
                  <mat-option
                    [value]="option.label"
                    [disabled]="option.disabled === true">
                    <div class="flex items-center gap-2">
                      <div class="flex flex-col leading-4 min-w-0 flex-1">
                        <span>{{ option.label }}</span>
                        @if (option.additionalInfoText) {
                          <span class="text-xs text-accent">{{ option.additionalInfoText }}</span>
                        } @else if (option.additionalInfo?.length) {
                          <span class="flex flex-wrap text-xs text-accent">
                            @for (info of option.additionalInfo; track info.label) {
                              <span>
                                <span class="text-hint">{{ info.label }}:</span> {{ info.value }}
                              </span>
                            }
                          </span>
                        }
                        @if (option.autoFillLinkedText) {
                          <span class="text-xs text-accent">{{ option.autoFillLinkedText }}</span>
                        }
                      </div>
                      @if (option.unavailableReason) {
                        <span class="ml-auto shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none bg-warning bg-opacity-12 text-warning-800">
                          {{ option.unavailableReason }}
                        </span>
                      }
                    </div>
                  </mat-option>
                }
              }

              @if (searched() && filteredOptions().length === 0) {
                <mat-option [disabled]="true"> Geen resultaten gevonden </mat-option>
              }
              @if (loading() && !onlyManualOptions()) {
                <mat-option disabled>
                  <div class="flex justify-center">
                    <mat-spinner diameter="24"></mat-spinner>
                  </div>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }
        </div>

        @if (showCreateButton()) {
        @if (!isDisabled()) {
          <ng-container
            *canCreateModule="{
              moduleId: linkedData()[0]?.module.id,
              show: true,
            }">
            <div class="flex-0 relative top-[3px] pl-2">
              <button
                mat-icon-button
                type="button"
                [matTooltipShowDelay]="400"
                [matTooltip]="props.label + ' toevoegen'"
                (click)="showCreateModal()">
                <mat-icon svgIcon="add"></mat-icon>
              </button>
            </div>
          </ng-container>
        }
      }
      </div>
      @if (selectedAdditionalInfo()) {
        <fuse-alert [type]="'info'">{{ selectedAdditionalInfo() }}</fuse-alert>
      }
      @if (selectedAutoFillInfo()) {
        <fuse-alert [type]="'info'"><span class="whitespace-pre-line">{{ selectedAutoFillInfo() }}</span></fuse-alert>
      }
      @if (field.props.description) {
        <mat-hint>{{ field.props.description }}</mat-hint>
      }
    </div>
    @if (field.formControl.errors) {
      <app-formly-error [field]="field"></app-formly-error>
    }
  `;

@Component({
  selector: 'formly-field-autocomplete',
  template: AUTOCOMPLETE_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIcon,
    MatIconButton,
    MatTooltip,
    CreateModuleRightsDirective,
    MatHint,
    MatAutocomplete,
    MatOption,
    MatOptgroup,
    MatSuffix,
    MatInput,
    MatLabel,
    MatFormField,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    FormlyErrorComponent,
    MatProgressSpinner,
    FormlyDataCyDirective,
    FuseAlertComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mdc-list-item__primary-text {
      width: 100%;
      display: inline-block;
    }

    ::ng-deep .cdk-overlay-pane {
      /* Do you changes here */
      min-width: 320px;
    }

    ::ng-deep .mat-mdc-optgroup-label .mdc-list-item__primary-text {
      font-weight: 500;
      span {
        font-size: 14px;
        font-weight: 400 !important;
      }
    }
  `,
})
export class FormlyFieldAutocomplete extends ClearableFieldType implements AfterViewInit, OnInit, OnDestroy {
  private modalService = inject(ModalService);
  private formService = inject(FormService);
  protected destroy$ = injectDestroy();
  protected dynamicOptionsService = inject(DynamicOptionsService);
  readonly #cascadingAutoFillService = inject(CascadingAutoFillService);
  readonly #modulesStore = inject(ModulesStore);
  readonly #utilityService = inject(UtilityService);

  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  private scrollListener: (() => void) | null = null;

  // Pagination state
  protected pageSize = 25; // Increased for better performance with large datasets
  protected currentOffset = signal(0);
  isLoading = signal(false);
  loading = signal(false);
  protected hasMore = signal(true);
  private currentSearchTerm = signal('');
  private lastDisabledState: boolean | undefined;
  // Track if all options have been loaded (for lazy loading)
  private allOptionsLoaded = signal(false);
  // Tracks the value ID we're currently fetching for validation, to prevent duplicate requests
  private validatingValueId: string | null = null;

  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.filteredOptions().length === 0 || this.dynamicFilterValue() == null)
  );

  showCreateButton = computed(() => {
    return this.linkedData()[0]?.module != undefined && this.field.props.showCreateButton;
  });

  onlyManualOptions = computed(() => {
    return (
      (this.field.props?.optionsArray?.length > 0 && this.field.props?.optionsArray[0].optionsType === 'manual') ||
      this.field.props?.optionsArray === undefined
    );
  });

  hasGrouping = computed(() => {
    return this.field.props?.optionsArray?.[0]?.groupByField != null;
  });

  groupedOptions = computed(() => {
    const options = this.filteredOptions();
    if (!this.hasGrouping() || options.length === 0) {
      return [];
    }

    const groups = new Map<string, any[]>();
    for (const option of options) {
      const groupName = option.group ?? 'Overig';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(option);
    }

    return Array.from(groups.entries()).map(([name, opts]) => ({
      name,
      options: opts,
    }));
  });

  searchField = new FormControl();
  filteredOptions = signal([]);
  searched = signal(false);
  loaded = signal(false);
  private isSearching = false;

  // Signal to track current form control value for reactivity
  protected currentValue = signal<any>(null);

  // Computed signal for selected option's additional info
  selectedAdditionalInfo = computed(() => {
    const value = this.currentValue();
    if (!value) return null;

    const matchingOption = this.filteredOptions().find(
      option => option.value === value || (value?.id && option.value?.id === value.id)
    );

    return matchingOption?.additionalInfoText || null;
  });

  selectedAutoFillInfo = computed(() => {
    const value = this.currentValue();
    if (!value) return null;

    const matchingOption = this.filteredOptions().find(
      option => option.value === value || (value?.id && option.value?.id === value.id)
    );

    return matchingOption?.autoFillLinkedTextGrouped ?? matchingOption?.autoFillLinkedText ?? null;
  });

  linkedData = signal([]);
  private dynamicFilterValue = signal(undefined);
  private cleanup?: () => void;
  private moduleId = computed(() => this.options.formState?.entity?.id);

  setupScrollHandler() {
    // Clean up previous listener if it exists
    if (this.scrollListener) {
      this.removeScrollListener();
    }

    // Wait for the panel to be available in the DOM
    setTimeout(() => {
      const panel = this.matAutocomplete.panel?.nativeElement;
      if (!panel) return;

      const handleScroll = () => {
        if (!this.hasMore() || this.loading()) return;

        const scrollPosition = panel.scrollTop + panel.clientHeight;
        const scrollThreshold = panel.scrollHeight - 50; // Load more when 50px from bottom

        if (scrollPosition >= scrollThreshold) {
          this.loadMoreOptions();
        }
      };

      panel.addEventListener('scroll', handleScroll);
      this.scrollListener = () => panel.removeEventListener('scroll', handleScroll);
    });
  }

  removeScrollListener() {
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }
  }

  ngOnInit() {
    // Initialize current value signal for reactivity
    this.currentValue.set(this.field.formControl.value);

    this.initializeDynamicOptions();
    this.initializeOptions();
    this.setupValueChanges();
  }

  /**
   * Lazy loading strategy for options:
   * - No value: Don't fetch anything, wait for autocomplete open
   * - Has value: Fetch only the current value to validate it exists
   * - Full options are loaded when autocomplete opens
   */
  private initializeOptions(): void {
    if (this.isDisabled()) {
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    const currentValue = this.field.formControl.value;

    // No initial value - don't load anything, wait for autocomplete open
    if (!currentValue) {
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    // For manual options (direct props.options), use initializeStaticOptions
    // This handles fields like "Veldtype" that have static options defined directly
    if (this.onlyManualOptions()) {
      this.initializeStaticOptions();
      this.updateSearchFieldValue();
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    // Has initial value - fetch only that single option to validate
    this.isLoading.set(true);
    this.loaded.set(false);

    this.dynamicOptionsService.fetchSingleOptionByValue(this.field, currentValue).subscribe({
      next: options => {
        if (this.isDisabled()) {
          this.isLoading.set(false);
          this.loaded.set(true);
          return;
        }

        this.filteredOptions.set(options ?? []);
        this.updateSearchFieldValue();
        this.isLoading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        this.isLoading.set(false);
        this.loaded.set(true);
      },
    });
  }

  ngOnDestroy() {
    this.cleanup?.();
    this.removeScrollListener();
  }

  ngAfterViewInit() {
    this.setupInitialState();

    // Subscribe to panel closing to clean up scroll listener
    this.matAutocomplete?.closed.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.removeScrollListener();
    });
  }

  private loadMoreOptions(): void {
    if (this.onlyManualOptions()) {
      return;
    }

    this.loading.set(true);
    const nextOffset = this.currentOffset() + this.pageSize;

    this.dynamicOptionsService
      .fetchOptionsForValue(
        this.dynamicFilterValue(),
        this.linkedData(),
        this.currentSearchTerm(),
        this.field,
        nextOffset,
        this.pageSize
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: newOptions => {
          this.loaded.set(true);
          if (newOptions.length < this.pageSize) {
            this.hasMore.set(false);
          }
          this.loading.set(false);

          this.currentOffset.set(nextOffset);

          this.filteredOptions.update(current => [...current, ...newOptions]);
        },
        error: () => {
          this.loading.set(false);
          this.hasMore.set(false);
        },
      });
  }

  private initializeStaticOptions(): void {
    if (this.isDisabled()) {
      this.loaded.set(true);
      return;
    }

    if (this.dynamicOptionsService.initializeStaticOptions(this.field)) {
      this.filteredOptions.set(this.dynamicOptionsService.initializeStaticOptions(this.field));
    }
    this.validateOptionExists();
    this.loaded.set(true);
  }

  private initializeDynamicOptions(): void {
    if (this.onlyManualOptions() && !isObservable(this.field.props.options)) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        lazyLoad: true, // Skip initial fetch, load options when autocomplete opens
        onOptionsUpdate: options => {
          if (this.isDisabled()) {
            if (this.isLoading()) {
              this.isLoading.set(false);
            }
            return;
          }

          if (options != null) {
            this.filteredOptions.set(options);
            if (this.isLoading()) {
              this.isLoading.set(false);
            }
            this.updateSearchFieldValue();
          }
          this.validateOptionExists();
          this.setDisabledState(this.isDisabled());
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterValue.set(value);
          this.validateOptionExists();
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;
  }

  protected setupValueChanges(): void {
    // Handle search field changes
    this.searchField.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.handleSearchValueChange(value));

    // Handle form control changes
    this.field.formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(value => {
      this.currentValue.set(value);
      this.updateSearchFieldValue();
      this.validateOptionExists();
    });

    this.field.formControl.statusChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(status => {
      requestAnimationFrame(() => {
        const shouldBeDisabled = this.isDisabled();
        if (this.lastDisabledState !== this.isDisabled()) {
          this.lastDisabledState = shouldBeDisabled;
          this.setDisabledState(shouldBeDisabled);
        }
      });
    });
  }

  private setupInitialState(): void {
    requestAnimationFrame(() => {
      this.updateSearchFieldValue();
      this.setDisabledState(this.isDisabled());
    });
  }

  private handleSearchValueChange(value: string): void {
    // Every branch below writes the entry control on behalf of the user, so each one has to mark
    // it dirty (#21026) — the visible input is bound to `searchField`, never to the entry control,
    // so Angular's own dirty-tracking never fires here. The `{ emitEvent: false }` writes get
    // `markUserEdit` AFTER the write: the dirty transition is then the only event that fires, and
    // it must carry the new value (see user-edit.util.ts).
    if (!this.props.requireSelection && value !== null) {
      this.field.formControl.patchValue(value, { emitEvent: false });
      markUserEdit(this.field.formControl);
      this.currentValue.set(value || null);
    }

    const matchingOption = this.filteredOptions().find(option => option.label === value);
    if (matchingOption) {
      commitUserValue(this.field.formControl, matchingOption.value);
      // User selected an option, reset flag so next dropdown open shows all options
      this.isSearching = false;
      return;
    }

    if (this.props.requireSelection && value === '') {
      this.field.formControl.patchValue(null, { emitEvent: false });
      markUserEdit(this.field.formControl);
      this.currentValue.set(null);
    }

    // Trim whitespace so trailing/leading spaces don't trigger empty backend results
    const searchTerm = (this.searchField.value ?? '').trim();

    // Set flag to prevent duplicate request from onAutocompleteOpened
    this.isSearching = true;
    this.fetchOptions(searchTerm, 0, this.pageSize);
  }

  fetchOptions(searchTerm = '', offset = 0, limit = 25) {
    if (this.isDisabled()) {
      this.loading.set(false);
      this.isLoading.set(false);
      return;
    }

    if (this.onlyManualOptions()) {
      this.initializeStaticOptions();
      if (searchTerm != '' && searchTerm != null) {
        this.filteredOptions.set(
          this.filteredOptions().filter(option => option.label.toLowerCase().includes(searchTerm?.toLowerCase()))
        );
      }
      return;
    }

    // Reset pagination state
    this.currentOffset.set(0);
    this.hasMore.set(true);
    this.currentSearchTerm.set(searchTerm);
    this.loading.set(true);

    this.dynamicOptionsService
      .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), searchTerm, this.field, offset, limit)
      .subscribe(options => {
        if (this.isDisabled()) {
          this.loading.set(false);
          this.isLoading.set(false);
          return;
        }

        this.filteredOptions.set(options);
        this.searched.set(true);
        this.validateOptionExists();
        this.loading.set(false);
        this.loaded.set(true);
        this.isLoading.set(false);

        // Check if we have less options than requested
        if (options.length < this.pageSize) {
          this.hasMore.set(false);
        }
      });
  }

  protected updateSearchFieldValue(): void {
    // First check in current options
    const currentValue = this.field.formControl.value;

    if (currentValue == null || currentValue == '') {
      this.searchField.patchValue(null, { emitEvent: false });
      return;
    }

    const matchingOption = this.filteredOptions().find(
      option => option.value === currentValue || (currentValue?.id && option.value?.id === currentValue.id)
    );

    if (matchingOption) {
      this.searchField.patchValue(matchingOption.label, { emitEvent: false });
      return;
    }

    if (this.filteredOptions().length === 0 && this.isLoading()) {
      return;
    }

    const moduleLinkedField =
      this.linkedData()?.[0]?.moduleLinkedField ?? this.field.props?.optionsArray?.[0]?.moduleLinkedField;

    if (currentValue == null) {
      this.searchField.patchValue(null, { emitEvent: false });
      return;
    }

    const label =
      typeof currentValue === 'string'
        ? currentValue
        : (currentValue?.selectedInput ??
          (moduleLinkedField ? UtilityService.keyPathToValue(currentValue, moduleLinkedField) : ''));

    if (!this.props.requireSelection) {
      this.searchField.patchValue(label ?? null, { emitEvent: false });
      return;
    }

    if (label != null) {
      this.searchField.patchValue(label, { emitEvent: false });
    }
  }

  private validateOptionExists(): void {
    const currentValue = this.field.formControl.value;

    if (!currentValue || !this.loaded()) {
      return;
    }

    // Fast path: value is in the currently loaded options → validate normally (clears error)
    const found = this.filteredOptions().find(opt => opt.value?.id === currentValue?.id || opt.value === currentValue);
    if (found) {
      this.field = FormlyErrorService.validateOptions(this.field, this.filteredOptions(), currentValue);
      return;
    }

    // Value not found in loaded options. Before declaring it invalid, try to fetch it
    // by searching with its display text. This handles paginated lazy-loaded options
    // where the current value might simply not be in the first page (e.g. after a
    // route reorder, or when the field was just initialized with a saved value).
    this.#fetchAndValidateCurrentValue(currentValue);
  }

  #fetchAndValidateCurrentValue(currentValue: any): void {
    // Manual options are static — fetching is not possible, fall through to normal validation
    if (this.onlyManualOptions()) {
      this.field = FormlyErrorService.validateOptions(this.field, this.filteredOptions(), currentValue);
      return;
    }

    const displayValue =
      typeof currentValue === 'string' ? currentValue : (currentValue?.selectedInput ?? currentValue?.name);

    if (!displayValue || typeof displayValue !== 'string') {
      // No display text to search with — fall through to normal validation
      this.field = FormlyErrorService.validateOptions(this.field, this.filteredOptions(), currentValue);
      return;
    }

    // Prevent duplicate fetches for the same value
    const valueId = currentValue?.id ?? displayValue;
    if (this.validatingValueId === valueId) {
      return;
    }
    this.validatingValueId = valueId;

    this.dynamicOptionsService
      .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), displayValue, this.field, 0, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: options => {
          this.validatingValueId = null;

          // Merge any new options into filteredOptions without removing existing ones,
          // so the dropdown still shows the originally loaded page when the user opens it.
          const existingIds = new Set(
            this.filteredOptions()
              .map(o => o.value?.id)
              .filter(Boolean)
          );
          const newOptions = options.filter(o => o.value?.id && !existingIds.has(o.value.id));
          if (newOptions.length > 0) {
            this.filteredOptions.update(current => [...current, ...newOptions]);
          }

          // Now validate against the (possibly merged) options list
          this.field = FormlyErrorService.validateOptions(this.field, this.filteredOptions(), currentValue);
          this.updateSearchFieldValue();
        },
        error: () => {
          this.validatingValueId = null;
          // Fetch failed — fall through to normal validation against existing options
          this.field = FormlyErrorService.validateOptions(this.field, this.filteredOptions(), currentValue);
        },
      });
  }

  showCreateModal(): void {
    const module = this.linkedData()[0].module;
    const entry = this.prefillAutocompleteValues();

    // Create-Form runner: only a WIZARD create-form runs through the runner here. A flat create-form
    // only persists a FORM-module submission (no linked TARGET-module entry), so it falls back to the
    // default entry modal below — which creates the proper target entry the autocomplete needs.
    // Field-level createFormId (when set, '' = Standaard) takes precedence over the linked module's
    // own createFormId; a flat field-level form falls back exactly like the module-level case.
    const fieldCreateFormId = this.field.props?.createFormId as string | undefined;
    const runner = this.modalService.resolveCreateRunner(module, entry, fieldCreateFormId || undefined);
    if (runner.status === 'denied') {
      return; // access denied — the user has already been notified; do not fall back to default create
    }
    if (runner.status === 'opened') {
      runner.ref.afterClosed().subscribe((result: { createdEntries?: Record<string, Entry> } & Entry) => {
        if (result == null) {
          return; // dialog cancelled
        }
        const created = this.#extractCreatedEntry(result);
        if (created) {
          this.#applyCreatedEntry(created);
        } else {
          this.#utilityService.showMessage('Er is geen item aangemaakt.');
        }
      });
      return;
    }

    if (runner.status !== 'fallback') {
      // 'pending': the modal service retries and opens the runner itself once the ModulesStore
      // settles — opening the default modal here would stack a second (wrong) dialog on top.
      return;
    }

    // status === 'fallback': default create flow (not configured or flat form).
    const modal = this.modalService.openEntryInModal(module, entry, module.singularName, true);
    modal.afterClosed().subscribe((result: { entry: Entry; files: PendingFile[] }) => {
      if (result?.entry) {
        this.#applyCreatedEntry(result.entry);
      }
    });
  }

  /**
   * Normalize a runner close-result into the created target-module entry.
   * - Wizard runner closes with `{ createdEntries }` → resolve the entry created in this field's
   *   own linked module (the map also holds earlier steps' entries, and they come first).
   * - Flat form runner closes with the saved entry shape (`{ entry, ... }`).
   */
  #extractCreatedEntry(result: CreateRunnerCloseResult & Partial<Entry>): Entry | null {
    if (result?.createdEntries) {
      // Prefer the entry created in THIS autocomplete's linked module (a wizard may create entries
      // in several modules); the shared resolver owns that rule and the single-entry fallback.
      return resolveCreatedEntry(result.createdEntries, this.linkedData()[0]?.module?.id);
    }
    return result?.entry ? (result as Entry) : null;
  }

  /** Patch the autocomplete value from a created entry and refresh its options. */
  #applyCreatedEntry(createdEntry: Entry): void {
    const value = UtilityService.keyPathToValue(createdEntry, this.linkedData()[0].moduleLinkedField);
    if (value === undefined) {
      return;
    }
    createdEntry.selectedInput = value;
    this.searchField.patchValue(value, { emitEvent: false });
    this.field.formControl.patchValue(createdEntry);

    this.dynamicOptionsService
      .fetchOptionsForValue(
        this.dynamicFilterValue(),
        this.linkedData(),
        this.searchField.value,
        this.field,
        0,
        this.pageSize
      )
      .subscribe(options => {
        this.filteredOptions.set(options);
        this.validateOptionExists();
      });
  }

  private prefillAutocompleteValues(): Entry {
    let newEntry: Entry = {
      id: undefined,
      entry: { archived: false },
    };

    // Handle defaultValuesAutocomplete
    if (this.field.props.defaultValuesAutocomplete) {
      newEntry = this.formService.fillEntryWithMappingItems(
        newEntry,
        this.field.props.defaultValuesAutocomplete,
        this.moduleId(),
        this.options.formState.entry
      );
    }

    return newEntry;
  }

  clearValue(markAsInteracted = true): void {
    this.searchField.patchValue('', { emitEvent: false });
    this.field.formControl.patchValue(null);
    if (markAsInteracted) {
      this.field.formControl.markAsDirty();
      this.field.formControl.markAsTouched();
    }
  }

  /** Deviates: free-text search input holds a clearable value even when the bound control is empty. */
  override hasClearableValue(): boolean {
    const value = this.field.formControl.value;
    const searchValue = this.searchField.value;
    return (
      (value !== null && value !== undefined && value !== '') ||
      (searchValue !== null && searchValue !== undefined && searchValue !== '')
    );
  }

  searchWithoutFilter() {
    this.fetchOptions('', 0, this.pageSize);
  }

  /**
   * Called when the input field receives focus - loads options if not already loaded
   */
  onInputFocus(): void {
    // Only fetch if we don't have options yet
    if (this.filteredOptions().length === 0 && !this.loading()) {
      this.searchWithoutFilter();
    }
  }

  onAutocompleteOpened() {
    // Skip searchWithoutFilter if we just triggered a search from handleSearchValueChange
    // This prevents duplicate requests when user is actively typing
    if (this.isSearching) {
      this.isSearching = false;
    } else {
      this.searchWithoutFilter();
    }
    if (!this.onlyManualOptions()) {
      this.setupScrollHandler();
    }
  }

  /**
   * Handle option selection directly from mat-autocomplete.
   * This bypasses the distinctUntilChanged issue when user types exact match
   * and then selects the same value from dropdown.
   */
  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedLabel = event.option.value;
    const matchingOption = this.filteredOptions().find(option => option.label === selectedLabel);

    if (matchingOption) {
      commitUserValue(this.field.formControl, matchingOption.value);
      this.searchField.patchValue(matchingOption.label, { emitEvent: false });
      this.isSearching = false;

      this.#triggerCascadingAutoFill(matchingOption.value);
    }
  }

  #triggerCascadingAutoFill(selectedValue: unknown): void {
    const mappings = this.field.props?.copyFieldsOnSelect;
    if (!mappings?.length) return;

    if (!selectedValue || typeof selectedValue !== 'object' || !('id' in selectedValue)) return;

    const sourceModuleCode = this.linkedData()?.[0]?.moduleCode ?? this.field.props?.optionsArray?.[0]?.moduleCode;
    if (!sourceModuleCode) return;

    const currentEntry = this.options.formState?.entry;
    if (!currentEntry) return;

    const moduleId = this.moduleId();
    const generatedForm = moduleId ? this.#modulesStore.generatedForms()[moduleId] : null;
    if (!generatedForm) return;

    this.#cascadingAutoFillService
      .processCascadingAutoFill(
        selectedValue as { id: string; selectedInput?: string },
        sourceModuleCode,
        mappings,
        currentEntry,
        generatedForm,
        this.field
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        for (const result of results) {
          this.#applyAutoFillResult(result);
        }
      });
  }

  #findFormControlByKeyPath(keyPath: string, repeatIndex?: number[]): AbstractControl | null {
    // Walk up to root field
    let rootField: FormlyFieldConfig = this.field;
    while (rootField.parent) {
      rootField = rootField.parent;
    }

    const keys = keyPath.replace(/^entry\./, '').split('.');
    return this.#searchFieldGroupForKey(rootField.fieldGroup, keys, 0, repeatIndex ?? [], 0);
  }

  /**
   * Recursively search through fieldGroups, transparently skipping keyless wrappers (tabs, pages, steps).
   */
  #searchFieldGroupForKey(
    fields: FormlyFieldConfig[] | undefined,
    keys: string[],
    keyIndex: number,
    repeatIndex: number[],
    indexPtr: number
  ): AbstractControl | null {
    if (!fields || keyIndex >= keys.length) return null;

    for (const field of fields) {
      if (field.key === keys[keyIndex]) {
        // Last key → return formControl
        if (keyIndex === keys.length - 1) {
          return field.formControl ?? null;
        }

        // Repeat field: use repeatIndex to pick the right row
        if (field.fieldArray && field.fieldGroup && indexPtr < repeatIndex.length) {
          const idx = repeatIndex[indexPtr];
          if (idx >= field.fieldGroup.length) return null;
          return this.#searchFieldGroupForKey(
            field.fieldGroup[idx]?.fieldGroup,
            keys,
            keyIndex + 1,
            repeatIndex,
            indexPtr + 1
          );
        }

        return this.#searchFieldGroupForKey(field.fieldGroup, keys, keyIndex + 1, repeatIndex, indexPtr);
      }

      // Keyless wrapper (tab, page, step): dive into its fieldGroup without advancing the key index
      if (!field.key && field.fieldGroup) {
        const result = this.#searchFieldGroupForKey(field.fieldGroup, keys, keyIndex, repeatIndex, indexPtr);
        if (result) return result;
      }
    }

    return null;
  }

  #applyAutoFillResult(result: AutoFillResult): void {
    const formControl = this.#findFormControlByKeyPath(result.targetFieldKeyPath, result.repeatIndex);
    if (!formControl || formControl.disabled) return;

    // Update formState.entry
    const currentEntry = this.options.formState?.entry;
    if (currentEntry) {
      if (result.repeatIndex?.length) {
        // For repeat fields: navigate manually using the array indices
        this.#setValueAtNestedPath(currentEntry, result.targetFieldKeyPath, result.value, result.repeatIndex);
      } else {
        UtilityService.setKeyValuePath(currentEntry, result.targetFieldKeyPath, result.value);
      }
    }

    // Navigate the Formly field tree to find the form control (robust for nested repeat fields).
    // Dirty as well (#21026): a cascade only runs off a user selection, and the values it copies
    // into sibling fields are unsaved just like the selection that triggered them.
    commitUserValue(formControl, result.value);
  }

  /**
   * Navigate a nested object/array structure using a clean keyPath and separate array indices.
   * E.g. path "entry.general.users.users.user" with repeatIndex [0, 0]
   * navigates to entry.general.users[0].users[0].user
   */
  #setValueAtNestedPath(obj: any, keyPath: string, value: unknown, repeatIndex: number[]): void {
    const keys = keyPath.split('.');
    let current = obj;
    let indexPtr = 0;

    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
      if (current == null) return;
      // When we hit an array, use the next repeat index to select the right element
      if (Array.isArray(current) && indexPtr < repeatIndex.length) {
        current = current[repeatIndex[indexPtr++]];
        if (current == null) return;
      }
    }

    current[keys[keys.length - 1]] = value;
  }

  private setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.searchField.disable({ emitEvent: false });
      this.loading.set(false);
      this.isLoading.set(false);
    } else {
      this.searchField.enable({ emitEvent: false });
    }
  }

  protected readonly filter = filter;
}
